import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import URL from '../../constants/url';
import style from './HealthReport.module.css';
import GoalCheckCard from './GoalCheckCard';
import dayjs from 'dayjs';


const METRIC_LINKS = {
    calorie:    URL.FOOD_RECORD_MAIN,
    steps:      URL.STEPS_MAIN,
    exercise:   URL.EXERCISE_RECORD_MAIN,
    bloodpress: URL.BLOOD_PRESSURE_RECORD,
    bmi:        URL.BODY_MASS_RECORD,
};

const formatCheckupDate = (dateStr) => {
    if (!dateStr) return '-';

    const date = dayjs(dateStr, 'YYYYMMDD');
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];

    return `${date.format('YY.MM.DD')}(${weekdays[date.day()]})`;
};




const getCheckupLevel = (value, key) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    // 요단백 정성검사
    if (key === 'protein') {
        const protein = String(value).trim().toUpperCase().replace(/\s/g, '');

        let level;

        if (['NEGATIVE', 'NEG', 'TRACE'].includes(protein)) {
            level = 'good';
        } else if (['1+', '+1', '2+', '+2'].includes(protein)) {
            level = 'warn';
        } else if (['3+', '+3', '4+', '+4'].includes(protein)) {
            level = 'danger';
        } else {
            return null;
        }

        return {
            t: {
                good: '양호',
                warn: '주의',
                danger: '중점관리'
            }[level],
            level
        };
    }

    const num = Number(value);

    if (!Number.isFinite(num)) {
        return null;
    }

    let level;

    switch (key) {
        case 'egfr':
            level = num >= 90 ? 'good' : num >= 60 ? 'warn' : 'danger';
            break;
        case 'brssrTop':
            level = num < 130 ? 'good' : num < 140 ? 'warn' : 'danger';
            break;
        case 'brssrLwet':
            level = num < 80 ? 'good' : num < 90 ? 'warn' : 'danger';
            break;
        case 'bmi':
            level = num < 23 ? 'good' : num < 25 ? 'warn' : 'danger';
            break;
        case 'hba1c':
            level = num < 5.7 ? 'good' : num < 6.5 ? 'warn' : 'danger';
            break;
        default:
            return null;
    }

    return {
        t: {
            good: '양호',
            warn: '주의',
            danger: '중점관리'
        }[level],
        level
    };
};



const HealthReport = ({ subTab, dailyList, goalData, weeklyGoals = [], latestCheckup = {}, onSync }) => {

    const navigate = useNavigate();
    const [syncingIdx, setSyncingIdx] = useState(null);

    const egfrLevel = getCheckupLevel(latestCheckup?.egfr, 'egfr');
    const hba1cLevel = getCheckupLevel(latestCheckup?.hba1c, 'hba1c');
    const bmiLevel = getCheckupLevel(latestCheckup?.bmi, 'bmi');

    const getBloodPressureLevel = () => {
        const topLevel = getCheckupLevel(
            latestCheckup?.brssrTop,
            'brssrTop'
        );

        const lowLevel = getCheckupLevel(
            latestCheckup?.brssrLwet,
            'brssrLwet'
        );

        if (!topLevel && !lowLevel) {
            return null;
        }

        if (
            topLevel?.level === 'danger' ||
            lowLevel?.level === 'danger'
        ) {
            return { t: '중점관리', level: 'danger' };
        }

        if (
            topLevel?.level === 'warn' ||
            lowLevel?.level === 'warn'
        ) {
            return { t: '주의', level: 'warn' };
        }

        return { t: '양호', level: 'good' };
    };

    const bloodPressureLevel = getBloodPressureLevel();


    /**
     * 새로고침 버튼. 걸음수는 onSync 가 삼성 헬스 재조회 + 서버 동기화를 돌린다.
     * 회전 애니메이션은 최소 1.2초 유지한다 — 조회가 순식간에 끝나면 눌린 티가 안 난다.
     */
    const handleSync = async (e, idx, icon) => {
        e.stopPropagation();
        setSyncingIdx(idx);
        const spin = new Promise((resolve) => setTimeout(resolve, 1200));
        try {
            await Promise.all([onSync ? onSync(icon) : null, spin]);
        } finally {
            setSyncingIdx(null);
        }
    };

    if (subTab === 'daily') {
        return (
            <div className={style.section}>

                {weeklyGoals.map((g, i) => (
                    <GoalCheckCard
                        key={i}
                        title={g.title}
                        condition={g.condition}
                        days={g.days}
                        color={g.color}
                    />
                ))}
                
                <div className={style.metricList}>
                    {dailyList.map((m, idx) => (
                        <React.Fragment key={idx}>
                            <div className={style.metricRowBox} onClick={e => {
                                            e.stopPropagation();
                                            METRIC_LINKS[m.icon] && navigate(METRIC_LINKS[m.icon]);
                                        }}>
                                <div className={style.metricRow}>
                                    <span className={style.metricIcon} data-icon={m.icon} />
                                    <span className={style.metricLabel}>{m.label}</span>
                                    <span className={style.metricBadge} data-level={m.level}>{m.levelText}</span>
                                    <div className={style.metricValueWrap} >
                                        <span className={style.metricValue}>{m.value}</span>
                                        {m.goal && <span className={style.metricGoal}>{m.goal}</span>}
                                        {m.extra && <span className={style.metricExtra}>{m.extra}</span>}
                                        {m.unit && <span className={style.metricUnit}>{m.unit}</span>}
                                    
                                    </div>
                                    {m.btnType === 'sync' ? (
                                        <>
                                            <button
                                                className={`${style.metricSyncBtn} ${syncingIdx === idx ? style.syncing : ''}`}
                                                onClick={e => handleSync(e, idx, m.icon)}
                                            >
                                                <span className="blind">동기화</span>
                                            </button>
                                            {/* <button className={style.metricAddBtn} onClick={e => {
                                                e.stopPropagation();
                                                METRIC_LINKS[m.icon] && navigate(METRIC_LINKS[m.icon]);
                                            }}>+</button> */}
                                        </>
                                        
                                    ) : (
                                        <button className={style.metricAddBtn} onClick={e => {
                                            e.stopPropagation();
                                            METRIC_LINKS[m.icon] && navigate(METRIC_LINKS[m.icon]);
                                        }}>+</button>
                                    )}
                                </div>

                                {m.nutrients && (
                                    <div className={style.nutrientRow}>
                                        {m.nutrients.map((n, ni) => (
                                            <div key={ni} className={style.nutrientItem}>
                                                <span className={style.nutLabel}>{n.label}</span>
                                                <span className={style.nutValue}>{n.value}</span>
                                                <span className={style.nutGoal}>{n.goal}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            </div>
        );
    }

    if (subTab === 'goal') {
        return (
            <div className={style.section}>
                {/* <p className={style.checkupDate}>— {`26.08.01(목)`} 건강검진 기록입니다 —</p> */}
                <p className={style.checkupDate}>— {formatCheckupDate(latestCheckup?.recDe)} 건강검진 기록입니다 —</p>
                <div className={style.checkupList}>
                    {/* egfr */}


                    <div className={style.checkupRow}>
                        <span className={style.checkupIcon} data-icon={'egfr'} />
                        <span className={style.checkupLabel}>e-GFR</span>
                        {egfrLevel && (
                            <span className={style.metricBadge} data-level={egfrLevel.level}>
                                {egfrLevel.t}
                            </span>
                        )}
                        <div className={style.checkupValue}>
                            <strong>{latestCheckup.egfr ?? '-'}</strong>
                            <span>mL/min/1.73㎡</span>
                            <p className={style.checkupGoal}>{goalData.egfr} {goalData.egfrStdrCd === 'L' ? '이하' : '이상'}</p>
                        </div>
                    </div>
                    {/* 당화혈색소 */}
                    <div className={style.checkupRow}>
                        <span className={style.checkupIcon} data-icon={'hba1c'} />
                        <span className={style.checkupLabel}>당화혈색소</span>
                        {hba1cLevel && (
                            <span className={style.metricBadge} data-level={hba1cLevel.level}>
                                {hba1cLevel.t}
                            </span>
                        )}
                        <div className={style.checkupValue}>
                            <strong>{latestCheckup.hba1c ?? '-'}</strong>
                            <span>%</span>
                            <p className={style.checkupGoal}>{goalData.hba1c} {goalData.hba1cStdrCd === 'L' ? '이하' : '이상'}</p>
                        </div>
                    </div>
                    {/* 혈압 */}
                    <div className={style.checkupRow}>
                        <span className={style.checkupIcon} data-icon={'bloodpress'} />
                        <span className={style.checkupLabel}>혈압</span>
                        {bloodPressureLevel && (
                            <span className={style.metricBadge} data-level={bloodPressureLevel.level}>
                                {bloodPressureLevel.t}
                            </span>
                        )}
                        <div className={style.checkupValue}>
                            <strong>{latestCheckup.brssrTop ?? '-'} / {latestCheckup.brssrLwet ?? '-'}</strong>
                            <span>mmHg</span>
                            <p className={style.checkupGoal}>{goalData.brssrTop} {goalData.topStdrCd === 'L' ? '이하' : '이상'} / {goalData.brssrLwet} {goalData.lwetStdrCd === 'L' ? '이하' : '이상'}</p>
                        </div>
                    </div>
                    {/* 체질량 */}
                    <div className={style.checkupRow}>
                        <span className={style.checkupIcon} data-icon={'bmi'} />
                        <span className={style.checkupLabel}>체질량</span>
                        {bmiLevel && (
                            <span className={style.metricBadge} data-level={bmiLevel.level}>
                                {bmiLevel.t}
                            </span>
                        )}
                        <div className={style.checkupValue}>
                            <strong>{latestCheckup.bmi ?? '-'}</strong>
                            <span></span>
                            <p className={style.checkupGoal}>{goalData.bmi} {goalData.bmiStdrCd === 'L' ? '이하' : '이상'}</p>
                        </div>
                    </div>


                </div>
            </div>
        );
    }

    return null;
};

export default HealthReport;